import { fromJS } from 'immutable';
import { handleActions } from 'redux-actions';
import { builtinThemes, defaultSettings } from './config';
import {
  CHARTIQ_THEME_CHANGE_THEME,
  CHARTIQ_THEME_DELETE_THEME,
  CHARTIQ_THEME_RESTORE_THEMES,
  CHARTIQ_THEME_SAVE_THEME,
  CHARTIQ_THEME_SET_HELPER,
  CHARTIQ_THEME_TOGGLE_THEME_EDITOR,
  CHARTIQ_THEME_UPDATE_THEME,
} from './constants';

const CIQ = (window as any).CIQ;

const initialState = fromJS({
  currentThemeName: 'Default',
  currentThemeSettings: defaultSettings,
  showEditModal: false,
  themeHelper: null,
  themeList: builtinThemes.concat([{ name: '+ New Theme' }]),
});

function setTheme(themeHelper: any, theme: any) {
  const body = document.querySelector('body');
  if (!body) { return }
	if (theme.settings) {
		themeHelper.settings = CIQ.clone(theme.settings);
		themeHelper.update();
		body.className = theme.settings.className;
	} else if (theme.builtIn === true) {
		body.className = theme.className;
		const stx = themeHelper.params.stx;
		stx.styles = {};
		stx.chart.container.style.backgroundColor = '';
		if (stx.displayInitialized) {
			stx.headsUpHR();
			stx.clearPixelCache();
			stx.updateListeners('theme');
			stx.draw();
		}
	}
}

function updateThemeSettings(themeHelper: any, currentSettings: any, newParams?: any) {
  const settings = currentSettings.slice();
  const rgbaColor = (newParams && newParams.color) ? CIQ.hexToRgba('#' + newParams.color) : null;

  const newSettings = settings.map((setting: any) => {
    const newSetting: any = {
      class: setting.class,
      section: setting.section,
    };
    const swatches = setting.swatches.map((swatch: any) => {
      const newSwatch: any = {
        class: swatch.class,
        item: swatch.item,
      };
      let swatchNeedsNewColor = false;

      if (newParams && newParams.swatch && newParams.swatch === swatch.class) {
        swatchNeedsNewColor = true;
      }

      if (swatch.chart) {
        newSwatch.chart = swatch.chart;
        if (rgbaColor && swatchNeedsNewColor) { themeHelper.settings.chart[swatch.chart].color = rgbaColor }
        newSwatch.color = themeHelper.settings.chart[swatch.chart].color;
      } else if (swatch.chartType) {
        newSwatch.chartType = swatch.chartType;

        if (swatch.class.indexOf('Up') > -1 || swatch.class.indexOf('Down') > -1) {
          const capitalLetter = swatch.item.search(/(?=[A-Z])/);
          const direction = swatch.item.substring(capitalLetter).toLowerCase();
          const item = swatch.item.substring(0, capitalLetter);

          if (item !== 'candle') {
            if (rgbaColor && swatchNeedsNewColor) { themeHelper.settings.chartTypes[swatch.chartType][direction][item] = rgbaColor }
            newSwatch.color = themeHelper.settings.chartTypes[swatch.chartType][direction][item] || undefined;
          } else {
            if (rgbaColor && swatchNeedsNewColor) { themeHelper.settings.chartTypes[swatch.chartType][direction].color = rgbaColor }
            newSwatch.color = themeHelper.settings.chartTypes[swatch.chartType][direction].color || undefined;
          }
        } else {
          if (rgbaColor && swatchNeedsNewColor) { themeHelper.settings.chartTypes[swatch.chartType].color = rgbaColor }
          newSwatch.color = themeHelper.settings.chartTypes[swatch.chartType].color || undefined;
        }
      } else {
        newSwatch.color = undefined;
      }
      return newSwatch;
    });
    newSetting.swatches = swatches;
    return newSetting;
  });

  return newSettings;
}

export default handleActions({
  [CHARTIQ_THEME_CHANGE_THEME]: (state: any, { payload }: any) => {
    if (payload.theme.name.indexOf('+ New Theme') > -1) {
      return state
      .set('showEditModal', true);
    }
      const themeHelper = state.get('themeHelper');
      setTheme(themeHelper, payload.theme);
      const newThemeSettings = state.get('currentThemeName') ? updateThemeSettings(themeHelper, state.get('currentThemeSettings').toJS()) : defaultSettings;
      CIQ.localStorageSetItem('myChartCurrentThemeName', JSON.stringify(payload.theme.name));

      return state
      .set('currentThemeName', payload.theme.name)
      .set('currentThemeSettings', fromJS(newThemeSettings));
  },
  [CHARTIQ_THEME_DELETE_THEME]: (state: any, { payload }: any) => {
    const themeName = payload.theme.name;
    const newThemeList = state.get('themeList').toJS().slice();
    const themeIndex = newThemeList.findIndex((theme: any) => theme.name === themeName);
    newThemeList.splice(themeIndex, 1);
    CIQ.localStorageSetItem('myChartThemes', JSON.stringify(newThemeList));
    return state
    .set('themeList', fromJS(newThemeList))
    .set('currentThemeName', themeName)
    .set('showEditModal', false);
  },
  [CHARTIQ_THEME_RESTORE_THEMES]: (state: any) => {
    const restoredThemeList = JSON.parse(CIQ.localStorage.getItem('myChartThemes')) || state.get('themeList').toJS();
    const restoredCurrentThemeName = JSON.parse(CIQ.localStorage.getItem('myChartCurrentThemeName')) || restoredThemeList[0].name;
    let newState = state;
    if (restoredThemeList) {
      newState = newState.set('themeList', fromJS(restoredThemeList));
      const currentTheme = restoredThemeList.find((t: any) => t.name === restoredCurrentThemeName);

      if (currentTheme) {
        const themeHelper = newState.get('themeHelper');
        setTheme(themeHelper, currentTheme);
        newState = newState
        .set('currentThemeName', currentTheme.name)
        .set('currentThemeSettings', fromJS(updateThemeSettings(themeHelper, defaultSettings)));
      }
    }
    return newState;
  },
  [CHARTIQ_THEME_SAVE_THEME]: (state: any, { payload }: any) => {
    const item: any = {
      name: payload.name,
      settings: payload.theme,
    };
    const body = document.querySelector('body')
    if (body) {
      item.className = body.className;
    }
    const newThemeList = state.get('themeList').toJS().slice();
    const existsIndex = newThemeList.findIndex((t: any) => t.name.toUpperCase() === payload.name.toUpperCase());

    if ((existsIndex > -1 && newThemeList[existsIndex].builtIn === true) || payload.name === '+ New Theme') {
      return state;
    }

    if (existsIndex > -1) {
      newThemeList.splice(existsIndex, 1, item);
    } else {
      newThemeList.splice(newThemeList.length - 1, 0, item);
    }

    setTheme(state.get('themeHelper'), { settings: item.settings });

    CIQ.localStorageSetItem('myChartThemes', JSON.stringify(newThemeList));
    CIQ.localStorageSetItem('myChartCurrentThemeName', JSON.stringify(payload.name));

    return state
    .set('currentThemeName', payload.name)
    .set('themeList', fromJS(newThemeList))
    .set('showEditModal', false);
  },
  [CHARTIQ_THEME_SET_HELPER]: (state: any, { payload }: any) => {
    const { ciq } = payload;
    if (!ciq) { return state };
    const themeHelper = new CIQ.ThemeHelper({
      stx: ciq,
    });
    const newThemeSettings = updateThemeSettings(themeHelper, state.get('currentThemeSettings').toJS());
    return state
    .set('themeHelper', themeHelper) // save as mutable object
    .set('currentThemeSettings', fromJS(newThemeSettings));
  },
  [CHARTIQ_THEME_TOGGLE_THEME_EDITOR]: (state: any, { payload }: any) => {
    let newState = state;
    const showEditModal = state.get('showEditModal');
    if (!showEditModal && payload.theme) {
      const themeHelper = state.get('themeHelper');
      setTheme(themeHelper, payload.theme);
      const newThemeSettings = updateThemeSettings(themeHelper, state.get('currentThemeSettings').toJS());
      newState = newState
      .set('currentThemeName', payload.theme.name)
      .set('currentThemeSettings', fromJS(newThemeSettings));
    }

    return newState.set('showEditModal', !showEditModal);
  },
  [CHARTIQ_THEME_UPDATE_THEME]: (state: any, { payload }: any) => {
    const themeHelper = state.get('themeHelper');
    const newThemeSettings = updateThemeSettings(themeHelper, state.get('currentThemeSettings').toJS(), {
      color: payload.color,
      swatch: payload.swatch,
    });
    return state
    .set('currentThemeSettings', fromJS(newThemeSettings));

  },
}, initialState);
